import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faEnvelopeOpenText, faCommentDots } from '@fortawesome/free-solid-svg-icons';
import api from '../../utils/api';
import DateRangeFilter from '../../components/DateRangeFilter';
import { useRecentSearches } from '../../components/RecentSearches';
import { notificationDateRange } from '../../utils/notificationDateFilter';

const labels = { taskApprovals: 'Task approvals', leaveRequests: 'Leave requests', halfDayRequests: 'Half-day requests', wfhRequests: 'WFH requests', expenseRequests: 'Expense approvals' };
const alertLabels = { NO_TASK: 'No task after punch-in', MISSING_PUNCH: 'Attendance punch not recorded', LATE_PUNCH: 'Late punch-in', HALF_DAY_PUNCH: 'Half-day punch-in' };
const deliveryLabels = { sent: 'Sent', skipped: 'Not sent', failed: 'Failed', pending: 'Queued' };
// Kept in step with SOURCES in server/services/whatsappLog.service.js.
const whatsappSourceLabels = {
    TASK_OVERDUE: 'Task overdue', NO_TASK: 'No task after punch-in', MISSING_PUNCH: 'Attendance punch not recorded',
    LATE_PUNCH: 'Late punch-in', HALF_DAY_PUNCH: 'Half-day punch-in', APPROVAL_DIGEST: 'Daily approval summary',
    NUDGE: 'Task nudge', ANNOUNCEMENT: 'Company announcement', BIRTHDAY: 'Birthday wish'
};
const kinds = [['alerts', 'Attendance & task alerts'], ['digests', 'Daily approval summaries'], ['whatsapp', 'WhatsApp messages']];
const descriptions = {
    alerts: 'WhatsApp attendance and task alerts, recipients and recorded delivery outcomes.',
    digests: 'Daily approval summaries, recipients and recorded delivery outcomes.',
    whatsapp: 'Every WhatsApp message sent to people — alerts, overdue tasks, approval summaries, nudges and announcements — with its delivery outcome.'
};
const formatTime = value => new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });

export default function NotificationSent() {
    const [kind, setKind] = useState('alerts');
    const [alertType, setAlertType] = useState('');
    const [source, setSource] = useState('');
    const [status, setStatus] = useState('');
    const [searchInput, setSearchInput] = useState('');
    // The same list as the delivery history search in the app's WhatsApp screen.
    const recentSearch = useRecentSearches('delivery-history', { value: searchInput, onPick: setSearchInput });
    const [search, setSearch] = useState('');
    const [byStatus, setByStatus] = useState({});
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [datePreset, setDatePreset] = useState('All');
    const [customDates, setCustomDates] = useState({ from: '', to: '' });
    const [page, setPage] = useState(1);
    const [pages, setPages] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const { from, to } = notificationDateRange(datePreset, customDates);

    // Searching on every keystroke would run the cross-collection query per letter.
    useEffect(() => {
        const timer = setTimeout(() => {
            const value = searchInput.trim();
            if (value !== search) { setSearch(value); setPage(1); }
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput, search]);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        const dates = { ...(from ? { from } : {}), ...(to ? { to } : {}) };
        const request = kind === 'alerts'
            ? api.get('/notifications/admin/alerts', { params: { page, limit: 20, ...dates, ...(alertType ? { type: alertType } : {}) } })
            : kind === 'whatsapp'
                ? api.get('/notifications/admin/whatsapp', { params: { page, limit: 20, ...dates, ...(source ? { source } : {}), ...(status ? { status } : {}), ...(search ? { search } : {}) } })
                : api.get('/notifications/admin/digests', { params: { page, limit: 20, ...dates } });
        request
            .then(res => { if (active) { setRows(res.data.data || []); setPages(res.data.pagination.totalPages); setTotalRows(res.data.pagination.totalRecords || 0); setByStatus(res.data.byStatus || {}); } })
            .catch(() => { if (active) setError('Delivery history could not be loaded. Please try again.'); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [kind, alertType, source, status, search, page, from, to, refresh]);

    const switchKind = next => { setKind(next); setPage(1); setRows([]); };
    const alerts = kind === 'alerts';
    const whatsapp = kind === 'whatsapp';
    const whatsappFiltered = whatsapp && (source || status || search);
    const clearWhatsappFilters = () => { setSource(''); setStatus(''); setSearchInput(''); setSearch(''); setPage(1); };
    const changePreset = next => { setDatePreset(next); setPage(1); };
    const changeCustomDates = next => { setCustomDates(next); setPage(1); };

    return <section aria-label="Sent notifications" className="nc-layout">
        <aside className="nc-side">
            <p className="nc-side-label">Sent history</p>
            <nav aria-label="Sent notification kinds" className="nc-view-nav">
                {kinds.map(([key, label], index) =>
                    <button key={key} type="button" aria-pressed={kind === key} onClick={() => switchKind(key)}>
                        <span className="nc-view-icon"><FontAwesomeIcon icon={[faBell, faEnvelopeOpenText, faCommentDots][index]} /></span>
                        <span className="nc-view-copy"><strong>{label}</strong><small>{['Attendance and task notices', 'Scheduled approval digests', 'Delivery log by recipient'][index]}</small></span>
                    </button>)}
            </nav>
            <p className="nc-side-note">Delivery outcomes are recorded here for review. Open a message to see its full text.</p>
        </aside>
        <div className="nc-workspace">
        <div className="nc-section-heading nc-workspace-heading"><div><span className="nc-eyebrow">DELIVERY HISTORY</span><h2>{kinds.find(([key]) => key === kind)?.[1]}</h2><p>{descriptions[kind]}</p></div><span className="nc-heading-count">{!loading && !error ? totalRows : '—'}</span></div>
        <div className="nc-filter-panel">
            <div className="nc-filter-label">Sent during</div>
            <DateRangeFilter value={datePreset} onChange={changePreset} custom={customDates} onCustomChange={changeCustomDates} inline />
        </div>
        <div className="nc-toolbar nc-sent-toolbar">
            <div className="nc-sent-fields">
                {alerts && <label className="nc-field">Alert<select value={alertType} onChange={e => { setAlertType(e.target.value); setPage(1); }}>
                    <option value="">All alerts</option>
                    {Object.entries(alertLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                </select></label>}
                {whatsapp && <>
                    <label className="nc-field">Type<select value={source} onChange={e => { setSource(e.target.value); setPage(1); }}>
                        <option value="">All messages</option>
                        {Object.entries(whatsappSourceLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select></label>
                    <label className="nc-field">Status<select value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
                        <option value="">Any status</option>
                        {Object.entries(deliveryLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select></label>
                    <label className="nc-field">Recipient<input {...recentSearch.inputProps} type="search" placeholder="Name or employee ID" value={searchInput} onChange={e => setSearchInput(e.target.value)} /></label>
                    {recentSearch.menu}
                </>}
                {whatsappFiltered && <button type="button" className="nc-btn ghost" onClick={clearWhatsappFilters}>Clear filters</button>}
            </div>
            <button type="button" className="nc-btn ghost" disabled={loading} onClick={() => setRefresh(v => v + 1)}>Refresh</button>
        </div>
        <div className="nc-sent-result-line">{!loading && !error ? `${totalRows} record${totalRows === 1 ? '' : 's'}` : 'Delivery records'}</div>
        {whatsapp && !error && Object.keys(byStatus).length > 0 && <div className="nc-breakdown" aria-label="Delivery outcomes">
            {Object.entries(deliveryLabels).filter(([key]) => byStatus[key]).map(([key, label]) => <span key={key}>{label} <strong>{byStatus[key]}</strong></span>)}
        </div>}
        {error && <p className="nc-error" role="alert">{error}</p>}
        {loading ? <div className="nc-empty" role="status">Loading delivery history…</div> : !error && <>
            {!rows.length ? <div className="nc-empty">
                <h3>{alerts ? 'No alerts' : whatsapp ? 'No WhatsApp messages' : 'No sent summaries'}{from || to ? ' for this range' : whatsappFiltered ? ' match these filters' : ' yet'}</h3>
                <p>{alerts ? 'Alerts appear here when an employee misses a punch, punches in late, or has no task after punching in.'
                    : whatsapp ? 'Messages appear here for attendance alerts, overdue tasks, approval summaries, nudges and announcements. Missed-call and test messages are not logged.'
                        : 'Daily summaries appear here when there are pending approvals.'}</p>
                <button className="nc-btn ghost" onClick={() => { setDatePreset('All'); if (whatsapp) clearWhatsappFilters(); setRefresh(v => v + 1); }}>Refresh history</button>
            </div> : alerts ?
                <div className="nc-deliveries">{rows.map(row => <article className="nc-delivery" key={row._id}>
                    <header>
                        <div><span className="nc-label">{alertLabels[row.alertType] || row.alertType}</span><h3>{row.employee?.name || 'Former employee'}{row.employee?.employeeId ? ` (${row.employee.employeeId})` : ''}</h3></div>
                        <time dateTime={row.createdAt}>{formatTime(row.createdAt)}</time>
                    </header>
                    <div className="nc-breakdown"><span>Shift date <strong>{row.shiftDate}</strong></span><span>Recipients <strong>{row.recipients?.length || 0}</strong></span></div>
                    <div className="nc-channels">{(row.recipients || []).map((recipient, i) => <div key={recipient._id || i}>
                        <span className="nc-status">{recipient.user?.name || 'Former user'} · WhatsApp · {deliveryLabels[recipient.status] || recipient.status}</span>
                        {recipient.detail && <small>{recipient.detail}</small>}
                    </div>)}{!row.recipients?.length && <span className="nc-status">No recipients found</span>}</div>
                    {row.message && <details className="nc-message"><summary>View message</summary><p>{row.message}</p></details>}
                </article>)}</div> : whatsapp ?
                <div className="nc-deliveries">{rows.map(row => <article className="nc-delivery" key={row._id}>
                    <header>
                        <div><span className="nc-label">{row.sourceLabel}</span><h3>{row.recipient?.name || 'Former user'}{row.recipient?.employeeId ? ` (${row.recipient.employeeId})` : ''}</h3></div>
                        <time dateTime={row.sentAt}>{formatTime(row.sentAt)}</time>
                    </header>
                    <div className="nc-breakdown">
                        <span>Status <strong>{deliveryLabels[row.status] || row.status}</strong></span>
                        {row.phone && <span>Number <strong>{row.phone}</strong></span>}
                        {row.about && <span>About <strong>{row.about}</strong></span>}
                    </div>
                    {row.detail && <div className="nc-channels"><small>{row.detail}</small></div>}
                    {row.message && <details className="nc-message"><summary>View message</summary><p>{row.message}</p></details>}
                </article>)}</div> :
                <div className="nc-deliveries">{rows.map(row => <article className="nc-delivery" key={row._id}>
                    <header><div><span className="nc-label">Daily approval summary</span><h3>{row.recipientName}</h3></div><time>{row.digestDate}</time></header>
                    <div className="nc-breakdown">{Object.entries(row.counts || {}).filter(([key, n]) => labels[key] && n > 0).map(([key, n]) => <span key={key}>{labels[key]} <strong>{n}</strong></span>)}</div>
                    <div className="nc-channels">{(row.channels || []).map(channel => <div key={channel.channel}>
                        <span className="nc-status">{channel.channel === 'in_app' ? 'In-app' : 'WhatsApp'} · {channel.status}</span>
                        {channel.detail && <small>{channel.detail}</small>}
                    </div>)}{!row.channels?.length && <span className="nc-status">Delivery outcome not recorded</span>}</div>
                    <details className="nc-message"><summary>View message</summary><p>{row.message}</p></details>
                </article>)}</div>}
            {rows.length > 0 && <nav className="nc-pagination" aria-label="Sent history pages">
                <button className="nc-btn ghost" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Previous</button>
                <span>Page {page} of {pages}</span>
                <button className="nc-btn ghost" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>Next</button>
            </nav>}
        </>}
        </div>
    </section>;
}
